import { createApi } from '@reduxjs/toolkit/query/react';
import { userLoginTypes } from '../../types/userTypes';
import { Doc } from 'prettier';
import type { Permission } from '../../components/Admin/UpdatePermissions/RolesSection';
import type {
  Employees,
  Roles,
  User,
  DocumentFieldUpdate,
  deleteEmployee,
  activateDeactiavateEmployee,
} from '../../types/userTypes';
import { baseQueryWithReauth } from './baseFectch';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['queryAllusers', 'queryRoles', 'userById', 'getCurrentUser'],
  endpoints: (builder) => ({
    userSignup: builder.mutation<
      {
        message: string;
        status: boolean;
      },
      any
    >({
      query: (userdata) => ({
        url: '/register',
        method: 'POST',
        body: userdata,
        headers: {
          'content-type': 'application/json',
        },
      }),
    }),
    userLogin: builder.mutation<any, userLoginTypes>({
      query: (userdata) => ({
        url: '/login',
        method: 'POST',
        body: userdata,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getCurrentUser'],
    }),

    getRoles: builder.query<
      { status: string; log: string; roles: any[]; rolesNames: any[] },
      void
    >({
      query: () => ({
        url: '/roles',
        credentials: 'include',
      }),
      providesTags: ['queryRoles'],
    }),

    editRole: builder.mutation<
      any,
      { roleId: string; permissions: Permission[] }
    >({
      query: ({ roleId, permissions }) => ({
        url: `/roles/${roleId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { permissions },
      }),
    }),

    createRole: builder.mutation<
      any,
      { roleName: string; permissions: Permission[] }
    >({
      query: (newRole) => ({
        url: '/roles',
        method: 'POST',
        body: newRole,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    updateRole: builder.mutation({
      query: ({ employeeId, role }) => ({
        url: `/roles/${employeeId}`,
        method: 'PATCH',
        body: { role },
      }),
    }),

    getAllusers: builder.query<
      { status: string; log: string; employee: any[] },
      any
    >({
      query: (companyId) => ({
        url: `/getEmployees/${companyId}`,
        credentials: 'include',
      }),
      keepUnusedDataFor: 0,
    }),
    changeRole: builder.mutation<
      any,
      { email: string | undefined; role: string }
    >({
      query: (role) => ({
        url: '/updateRole',
        method: 'POST',
        body: role,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    changeEmploymentType: builder.mutation<
      any,
      { email: string | undefined; employmentType: string }
    >({
      query: (employmentType) => ({
        url: '/updateEmploymentType',
        method: 'POST',
        body: employmentType,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    getCurrentUser: builder.query<
      { status: string; log: string; User: User },
      void
    >({
      query: () => ({
        url: '/currentEmployee',
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      providesTags: ['getCurrentUser'],
    }),

    getVerifiedUsersBirthday: builder.query<
      { status: string; log: string; employees: Employees[] },
      any
    >({
      query: (companyId) => ({
        url: `/getVerifiedUsersBirthday/${companyId}`,
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
    }),
    getRolesEmployees: builder.query<
      { status: string; log: string; roles: Roles[] },
      any
    >({
      query: ({ companyId }) => ({
        url: `/rolesEmployees?companyId=${companyId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
    }),

    getUserById: builder.query<any, { _id: string | undefined | null }>({
      query: ({ _id }) => ({
        url: `/employee/${_id}`,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      providesTags: ['userById'],
    }),

    changeManager: builder.mutation<
      any,
      { email: string | undefined; manager_id: string | undefined }
    >({
      query: ({ email, manager_id }) => ({
        url: '/assignManager',
        method: 'POST',
        body: { email, manager_id },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    changeAdmin: builder.mutation<
      any,
      { email: string | undefined; admin_id: string | undefined }
    >({
      query: ({ email, admin_id }) => ({
        url: '/changeAdmin',
        method: 'POST',
        body: { email, admin_id },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    logout: builder.mutation<any, void>({
      query: () => ({
        url: '/logout',
        method: 'DELETE',
        credentials: 'include',
      }),
      // invalidatesTags: ["getCurrentUser"],
    }),
    uploadDocuments: builder.mutation<any, { formData: FormData }>({
      query: ({ formData }) => ({
        url: '/uploadDocuments',
        method: 'POST',
        body: formData,
        credentials: 'include',
      }),
    }),
    updateDocuments: builder.mutation<any, { formData: FormData }>({
      query: ({ formData }) => ({
        url: '/uploadDocuments',
        method: 'PUT',
        body: formData,
        credentials: 'include',
      }),
    }),
    updateField: builder.mutation<any, { body: DocumentFieldUpdate }>({
      query: ({ body }) => ({
        url: '/uploadDocuments',
        method: 'PATCH',
        body,
        credentials: 'include',
      }),
    }),
    updatePersonalInfo: builder.mutation<
      any,
      { data: any; companyId: string; userId: string | undefined }
    >({
      query: ({ data, companyId, userId }) => ({
        url: `/uploadPeronsalDetails/${companyId}/${userId}`,
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    verifyUser: builder.mutation<
      any,
      { email: string | undefined; status: string | undefined }
    >({
      query: ({ email, status }) => ({
        url: '/verify',
        method: 'POST',
        body: {
          email,
          status,
        },
      }),
      invalidatesTags: ['queryAllusers'],
    }),
    getDocuments: builder.query<{ list: Doc[] }, { userId: string }>({
      query: ({ userId }) => ({
        url: `/documents/?userId=${userId}`,
        credentials: 'include',
        method: 'GET',
      }),
    }),
    changeLeave: builder.mutation<any, any>({
      query: (leaveData) => ({
        url: '/changeLeaveData',
        method: 'POST',
        body: leaveData,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
    getAllEmployees: builder.query<any, string>({
      query: (companyId) => ({
        url: `/all?companyId=${companyId}`,
        credentials: 'include',
        method: 'GET',
      }),
      providesTags: ['queryAllusers'],
    }),

    addResignation: builder.mutation<any, any>({
      query: (data) => ({
        url: '/resign',
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
    getOffboardingEmployee: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/resign/employee/${employeeId}`,
      }),
    }),
    getAllOffboardingEmployees: builder.query<any, string>({
      query: (companyId) => ({
        url: `/resign?companyId=${companyId}`,
      }),
    }),

    getEmployeeOffBoardingProject: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/resign/project/${employeeId}`,
      }),
    }),
    addProject: builder.mutation<any, any>({
      query: (data) => ({
        url: `/resign/project`,
        method: 'POST',
        body: data,
      }),
    }),
    updateProjectStatus: builder.mutation<any, any>({
      query: ({ status, projectId }) => ({
        url: `/resign/project/${projectId}`,
        method: 'PUT',
        body: { status },
      }),
    }),
    employeeOffBoardingUpdate: builder.mutation<any, any>({
      query: ({ data, employeeId }) => ({
        url: `/resign/${employeeId}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    updateLastDay: builder.mutation<any, any>({
      query: ({ date, employeeId }) => ({
        url: `/resign/date/${employeeId}`,
        method: 'PUT',
        body: { date },
      }),
    }),
    updateResignationStatus: builder.mutation<any, any>({
      query: ({ status, employeeId }) => ({
        url: `/resign/status/${employeeId}`,
        method: 'PUT',
        body: { status },
      }),
    }),
    uploadExitForm: builder.mutation<any, any>({
      query: (formData) => ({
        url: `/resign/uploadExitForm`,
        method: 'POST',
        body: formData,
      }),
    }),
    addManualDocument: builder.mutation<any, any>({
      query: (formData) => ({
        url: `/resign/manual`,
        method: 'POST',
        body: formData,
      }),
    }),
    // router.get('/resign/manual', getAllDocuments)

    getEmployeeDetail: builder.query<any, any>({
      query: ({ employeeId }) => ({
        url: `/resign/detail/${employeeId}`,
      }),
    }),
    getAllManualDocs: builder.query<any, void>({
      query: () => ({
        url: `/resign/manual`,
      }),
    }),
    getPeopleData: builder.query<any, any>({
      query: (companyId) => ({
        url: `/people/${companyId}`,
      }),
    }),
    getLeaveRequests: builder.query<any, any>({
      query: (companyId) => ({
        url: `/people/leave/${companyId}`,
      }),
    }),
    getTodayAttendance: builder.query<any, any>({
      query: (companyId) => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return {
          url: `/people/list/${companyId}?timezone=${encodeURIComponent(
            timezone
          )}`,
        };
      },
    }),

    getEmployeeOverview: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/overview/${employeeId}`,
      }),
    }),
    updateEmployee: builder.mutation<any, any>({
      query: ({ employeeId, data }) => ({
        url: `/employee/edit/${employeeId}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    getMonthlyHours: builder.query<any, any>({
      query: ({ employeeId, timezone }) => ({
        url: `/employoyee/hours/${employeeId}?timezone=${timezone}`,
        method: 'GET',
      }),
    }),
    deleteEmployee: builder.mutation<any, deleteEmployee>({
      query: (employee) => ({
        url: `/delete/${employee.id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),

    updateSlackId: builder.mutation<
      any,
      { slack_id: string; employee_id: string }
    >({
      query: ({ slack_id, employee_id }) => ({
        url: '/updateSlackId',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { slack_id, employee_id },
      }),
    }),
    activateDeactiavateEmployee: builder.mutation<
      any,
      activateDeactiavateEmployee
    >({
      query: (employee) => ({
        url: `/activate-deactivate/${employee.id}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
  }),
});

export const {
  useUserLoginMutation,
  useUserSignupMutation,
  useUpdateEmployeeMutation,
  useGetRolesQuery,
  useEditRoleMutation,
  useUpdateRoleMutation,
  useCreateRoleMutation,
  useLazyGetAllusersQuery,
  useLazyGetVerifiedUsersBirthdayQuery,
  useGetRolesEmployeesQuery,
  useChangeRoleMutation,
  useChangeEmploymentTypeMutation,
  useGetUserByIdQuery,
  useUpdateSlackIdMutation,
  useChangeAdminMutation,
  useChangeManagerMutation,
  useLazyGetCurrentUserQuery,
  useGetCurrentUserQuery,
  useLogoutMutation,
  useUploadDocumentsMutation,
  useUpdatePersonalInfoMutation,
  useVerifyUserMutation,
  useChangeLeaveMutation,
  useDeleteEmployeeMutation,
  useGetDocumentsQuery,
  useUpdateDocumentsMutation,
  useUpdateFieldMutation,
  useActivateDeactiavateEmployeeMutation,
  useAddResignationMutation,
  useLazyGetOffboardingEmployeeQuery,
  useAddProjectMutation,
  useLazyGetEmployeeOffBoardingProjectQuery,
  useUpdateProjectStatusMutation,
  useEmployeeOffBoardingUpdateMutation,
  useUpdateLastDayMutation,
  useLazyGetEmployeeDetailQuery,
  useUpdateResignationStatusMutation,
  useUploadExitFormMutation,
  useAddManualDocumentMutation,
  useGetAllManualDocsQuery,
  useLazyGetPeopleDataQuery,
  useLazyGetTodayAttendanceQuery,
  useLazyGetMonthlyHoursQuery,
  useLazyGetLeaveRequestsQuery,
  useLazyGetEmployeeOverviewQuery,
  useLazyGetAllOffboardingEmployeesQuery,
  useLazyGetAllEmployeesQuery,
  useLazyGetUserByIdQuery,
} = authApi;
